import React from 'react';
import { TextField, DateField, Checkbox, Radio, SectionHeader, Dropdown, MetadataDropdown, SQLEditor, DataGrid, DynamicGrid } from './components';
import type { ComponentConfig, ValueChangeEvent } from './types';

interface ComponentRendererProps {
  config: ComponentConfig;
  value?: any;
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
  isDisabled?: boolean;
  allFormValues?: Record<string, any>; // For dynamic grid to access other form values
}

export const ComponentRegistry: React.FC<ComponentRendererProps> = ({
  config,
  value,
  onChange,
  pageId,
  error,
  isDisabled,
  allFormValues
}) => {
  // Don't render if component is not visible
  if (config.visible === false) {
    return null;
  }

  // Apply disabled state from checkbox control
  // If isDisabled is explicitly set (from checkbox control), use that value
  // Otherwise, fall back to the original config.disabled value
  const finalDisabled = isDisabled !== undefined ? isDisabled : config.disabled;

  switch (config.type) {
    case 'text':
      return (
        <TextField
          config={{ ...config, disabled: finalDisabled }}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'date':
      return (
        <DateField
          config={{ ...config, disabled: finalDisabled } as any}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'checkbox':
      return (
        <Checkbox
          config={{ ...config, disabled: finalDisabled } as any}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'radio':
      return (
        <Radio
          config={{ ...config, disabled: finalDisabled } as any}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'sectionHeader':
      return (
        <SectionHeader
          config={{ ...config, disabled: finalDisabled } as any}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'number':
      return (
        <TextField
          config={{ ...config, disabled: finalDisabled, additionalOptions: { ...config.additionalOptions, type: 'number' } }}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'dropdown':
      return (
        <Dropdown
          config={{ ...config, disabled: finalDisabled }}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'metadataDropdown':
      return (
        <MetadataDropdown
          config={{ ...config, disabled: finalDisabled }}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'sql':
      return (
        <SQLEditor
          config={{ ...config, disabled: finalDisabled } as any}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'grid':
      return (
        <DataGrid
          config={{ ...config, disabled: finalDisabled } as any}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
        />
      );
    
    case 'dynamicGrid':
      return (
        <DynamicGrid
          config={{ ...config, disabled: finalDisabled } as any}
          value={value}
          onChange={onChange}
          pageId={pageId}
          error={error}
          allFormValues={allFormValues || {}}
        />
      );
    
    default:
      logger.warn(`Unknown component type: ${(config as any).type}`);
      return null;
  }
};

// Helper function to validate component configuration
export const validateComponentConfig = (config: ComponentConfig): string[] => {
  const errors: string[] = [];
  
  if (!config.id) {
    errors.push('Component ID is required');
  }
  
  if (!config.label) {
    errors.push('Component label is required');
  }
  
  if (!['text', 'date', 'checkbox', 'dropdown', 'sql', 'grid', 'dynamicGrid'].includes(config.type)) {
    errors.push(`Invalid component type: ${config.type}`);
  }
  
  // Type-specific validations
  if (config.type === 'dropdown') {
    const dropdownOptions = config.additionalOptions as any;
    if (!dropdownOptions?.options || !Array.isArray(dropdownOptions.options)) {
      errors.push('Dropdown component must have options array in additionalOptions');
    }
  }
  
  return errors;
}; 